/**
 * Tables: the accessible, exact view of every chart, and the source of every
 * CSV export. The export uses the same rows and columns as the screen.
 */
import type { ReactNode } from 'react';
import { ShareBar } from '../charts/charts';
import { downloadCsv, toCsv, type CsvColumn } from '../lib/csv';
import { Button } from './ui';

export interface Column<T> extends CsvColumn<T> {
  render?: (row: T) => ReactNode;
  align?: 'left' | 'right';
  /** Draw a proportion bar under this column's value. */
  bar?: boolean;
}

export function DataTable<T>({
  rows,
  columns,
  csvName,
  empty,
  rowKey,
  onRowClick,
}: {
  rows: readonly T[];
  columns: Column<T>[];
  csvName?: string;
  empty?: ReactNode;
  rowKey: (row: T) => string;
  onRowClick?: (row: T) => void;
}) {
  if (rows.length === 0) return <>{empty ?? <p className="py-6 text-center text-sm text-gray-500">Nothing in this period.</p>}</>;
  const maxes = columns.map((c) => (c.bar ? Math.max(...rows.map((r) => Number(c.value(r)) || 0)) : 0));

  return (
    <div>
      <div className="overflow-x-auto -mx-2">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              {columns.map((c) => (
                <th key={c.header} scope="col" className={`px-2 py-2 font-medium text-gray-500 text-xs uppercase tracking-wide whitespace-nowrap ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={rowKey(row)}
                className={`border-b border-gray-100 last:border-0 ${onRowClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c, i) => (
                  <td key={c.header} className={`px-2 py-2.5 align-top ${c.align === 'right' ? 'text-right tabular' : 'text-left'}`}>
                    {c.render ? c.render(row) : String(c.value(row) ?? '—')}
                    {c.bar && (
                      <div className="mt-1.5">
                        <ShareBar value={Number(c.value(row)) || 0} max={maxes[i] as number} />
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {csvName && (
        <div className="mt-3 flex justify-end">
          <Button kind="quiet" small onClick={() => downloadCsv(csvName, toCsv(rows, columns))}>
            Download CSV
          </Button>
        </div>
      )}
    </div>
  );
}
